/**
 * GRN Allocation Repair — superuser drag-and-drop tool.
 *
 * Lists Type-4 PO lines whose GRNs were left on the synthetic line-0 anchor
 * (or any user-requested filter), and lets a superuser drop GRN cards onto
 * the correct schedule. Each drop fires the existing ``link-schedule``
 * action, which atomically:
 *   - rewrites the GRNScheduleAllocation row
 *   - updates the GRN's primary po_line_schedule FK
 *   - recomputes on_time_status via the supplier's OTIF window
 *   - reconciles shipped_qty / balance_qty / status on the affected schedules
 *   - invalidates the company dashboard cache + supplier performance cache
 *
 * Two full views, switched by the page tabs (``?view=arrears``):
 *   - GRN Repair — the quick-filter list (line 0, date buckets, all).
 *   - Arrears Repair — only true arrears: slots whose date has passed with a
 *     balance still above zero.
 *
 * Gate (same order as the backend ``_require_repair_access``): the CR-6274
 * GRN Repair module switch, a Scheduled-enabled install, plus ``is_superuser``
 * or ``is_staff``. The schedule condition binds superusers too — with no
 * schedule lines there are no allocations to repair. Every entry point is
 * hidden under the same rule. The verdict waits for the platform settings to
 * load, because their defaults would otherwise refuse for one frame.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, RefreshCw, Wrench, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import {
  grnRepairAPI,
  type RepairPoLineRow,
  type RepairFilterMode,
} from '../../lib/api/grn-repair';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { fmtDate, isArrearSchedule, localTodayIso } from './GrnRepairCards';
import { GrnRepairFilters } from './GrnRepairFilters';
import GrnRepairBoard, { ClosestMatchButton } from './GrnRepairBoard';
import { useGrnRepairLine } from './useGrnRepairLine';

export default function GrnAllocationRepair() {
  const { user } = useAuth();
  const {
    isDashboardTabEnabled, isFeatureEnabled, isLoading: menuSettingsLoading,
  } = useMenuSettings();
  // CR-6274: the GRN Repair module switch (Platform Admin → Features &
  // Modules), then a Scheduled-enabled install, then superuser or staff.
  // Mirrors the backend ``_require_repair_access`` gate in the same order.
  const moduleEnabled = isFeatureEnabled('grn_repair');
  const canRepair = moduleEnabled
    && isDashboardTabEnabled('scheduled')
    && (!!user?.is_superuser || !!user?.is_staff);

  // The platform settings arrive after mount, and their defaults say the
  // Scheduled tab is OFF (DEFAULT_DASHBOARD_TABS). Deciding before they land
  // therefore paints the refusal for a moment on an install that DOES have
  // the toolbox, then swaps to the board. Hold the verdict until the first
  // load completes. Latched, so a later reload (site / company switch) never
  // blanks the page the user is already working in.
  const [settingsSettled, setSettingsSettled] = useState(!menuSettingsLoading);
  useEffect(() => {
    if (!menuSettingsLoading) setSettingsSettled(true);
  }, [menuSettingsLoading]);
  const [rows, setRows] = useState<RepairPoLineRow[]>([]);
  const [count, setCount] = useState(0);
  const [scheduleCount, setScheduleCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [poSearch, setPoSearch] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [filterMode, setFilterMode] = useState<RepairFilterMode>('line0');
  // Page view: GRN Repair (quick filters) or Arrears Repair (true arrears only).
  const [searchParams, setSearchParams] = useSearchParams();
  const view: 'grn' | 'arrears' = searchParams.get('view') === 'arrears' ? 'arrears' : 'grn';
  const listMode: RepairFilterMode = view === 'arrears' ? 'arrears_repair' : filterMode;
  const [listLoading, setListLoading] = useState(false);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(() => {
    setListLoading(true);
    // Derive this week's Monday from the browser clock — identical to the
    // Schedules tabs' getThisWeekRange — so the date buckets share one week
    // boundary and the counts reconcile (no server-vs-browser midnight skew).
    const today = new Date();
    const daysSinceMonday = (today.getDay() + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysSinceMonday);
    monday.setHours(0, 0, 0, 0);
    const weekStart = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    const todayIso = localTodayIso();
    grnRepairAPI.listPoLines({
      po_search: poSearch || undefined,
      part_search: partSearch || undefined,
      supplier_search: supplierSearch || undefined,
      filter_mode: listMode,
      week_start: weekStart,
      today: todayIso,
      page,
      page_size: 50,
    })
      .then((resp) => {
        setRows(resp.data.results);
        setCount(resp.data.count);
        setScheduleCount(resp.data.schedule_count);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load PO lines'))
      .finally(() => setListLoading(false));
  }, [poSearch, partSearch, supplierSearch, listMode, page]);

  // Only fetch once the gate has actually passed: firing the list request
  // while the settings are still loading (or after a refusal) just earns a
  // 403 and an error banner the user should never see.
  useEffect(() => {
    if (settingsSettled && canRepair) fetchList();
  }, [fetchList, settingsSettled, canRepair]);

  // The drag board for the open PO line (detail, moves, splits, schedule
  // edits) is shared with the Import Monitor's ERP-mismatch repair modal.
  const line = useGrnRepairLine(fetchList);
  const { detail, detailLoading, detailError, fetchDetail, clearDetail } = line;

  // Arrears Repair: the open line's true-arrear slots, recomputed from each
  // refreshed detail — so a slot drops out as soon as a move balances it.
  const arrearSlots = view === 'arrears' && detail
    ? detail.schedules.filter((s) => isArrearSchedule(s, localTodayIso()))
    : [];
  // Bring the first arrear slot into view once per opened line (not on every refresh).
  const scrolledLineRef = useRef<number | null>(null);
  useEffect(() => {
    if (view !== 'arrears' || !detail || scrolledLineRef.current === detail.po_line.id) return;
    scrolledLineRef.current = detail.po_line.id;
    const first = detail.schedules.find((s) => isArrearSchedule(s, localTodayIso()));
    if (first) document.getElementById(`sched-zone-${first.id}`)?.scrollIntoView({ block: 'center' });
  }, [view, detail]);

  const switchView = (next: 'grn' | 'arrears') => {
    if (next === view) return;
    setSearchParams(next === 'arrears' ? { view: 'arrears' } : {});
    setPage(1);
    setSelectedLineId(null);
    clearDetail();
  };

  const [recalculating, setRecalculating] = useState(false);
  const performRecalculate = useCallback(async () => {
    setRecalculating(true);
    try {
      const resp = await grnRepairAPI.recalculate();
      toast.success(resp.data.detail);
      // Give the background job a moment, then refresh the visible counts.
      setTimeout(() => { fetchList(); if (detail) fetchDetail(detail.po_line.id); }, 4000);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Recalculation failed to start');
    } finally {
      setRecalculating(false);
    }
  }, [fetchList, fetchDetail, detail]);

  if (!settingsSettled) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!canRepair) {
    return (
      <div className="p-6">
        <Card className="p-6">
          <div className="flex gap-3 items-center text-red-600">
            <AlertCircle className="w-5 h-5" />
            <div>
              {/* CR-6274: say which condition failed. A switched-off module is
                  a deliberate admin choice and reads differently from an
                  account that simply lacks the role. */}
              {!moduleEnabled
                ? 'GRN Repair is not enabled on this platform.'
                : 'GRN Repair is only available on a Scheduled-enabled install, for superuser or staff accounts.'}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-3 h-[calc(100vh-72px)]">
      {/* Top bar — title + global recalculate */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Tabs value={view} onValueChange={(v) => switchView(v === 'arrears' ? 'arrears' : 'grn')}>
            <TabsList className="grid grid-cols-2 h-auto w-[360px] bg-gradient-to-r from-slate-50 to-slate-100">
              <TabsTrigger value="grn" className="py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-md">
                <div className="flex flex-col items-center gap-1">
                  <Wrench className={`h-5 w-5 ${view === 'grn' ? 'text-blue-600' : 'text-slate-500'}`} />
                  <span className={`text-xs font-medium ${view === 'grn' ? 'text-blue-700' : 'text-slate-600'}`}>GRN Repair</span>
                </div>
              </TabsTrigger>
              <TabsTrigger value="arrears" className="py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-md">
                <div className="flex flex-col items-center gap-1">
                  <CalendarClock className={`h-5 w-5 ${view === 'arrears' ? 'text-red-600' : 'text-slate-500'}`} />
                  <span className={`text-xs font-medium ${view === 'arrears' ? 'text-red-700' : 'text-slate-600'}`}>Arrears Repair</span>
                </div>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div>
            <div className="text-sm font-semibold">{view === 'arrears' ? 'Arrears Repair' : 'GRN Allocation Repair'}</div>
            <div className="text-xs text-muted-foreground">
              {view === 'arrears'
                ? 'True arrears only — schedule date passed and balance still above zero. Drag GRNs onto the right schedules to clear them.'
                : 'Drag GRNs onto schedules; edit, cancel or recalc to settle counts & OTIF.'}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={performRecalculate}
          disabled={recalculating}
          title="Reconcile shipped/balance, recompute active pointer + delivery dates, refresh OTIF and bust caches across all schedules. Does not re-point allocations."
        >
          {recalculating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Recalculate all
        </Button>
      </div>
      <div className="grid grid-cols-[340px_1fr] gap-4 flex-1 min-h-0">
      {/* LEFT — PO line list */}
      <Card className="p-3 flex flex-col overflow-hidden">
        <GrnRepairFilters
          filterMode={listMode}
          onFilterMode={(m) => { setPage(1); setFilterMode(m); }}
          showQuickFilters={view === 'grn'}
          poSearch={poSearch}
          onPoSearch={(v) => { setPage(1); setPoSearch(v); }}
          partSearch={partSearch}
          onPartSearch={(v) => { setPage(1); setPartSearch(v); }}
          supplierSearch={supplierSearch}
          onSupplierSearch={(v) => { setPage(1); setSupplierSearch(v); }}
          onRefresh={fetchList}
          count={count}
          scheduleCount={scheduleCount}
        />
        <div className="flex-1 overflow-auto">
          {listLoading && <Loader2 className="w-4 h-4 animate-spin mx-auto my-4" />}
          {rows.map((r) => (
            <button
              key={r.po_line_id}
              onClick={() => { setSelectedLineId(r.po_line_id); fetchDetail(r.po_line_id); }}
              className={`block w-full text-left px-2 py-1.5 rounded text-sm hover:bg-slate-100 ${selectedLineId === r.po_line_id ? 'bg-blue-50' : ''}`}
            >
              <div className="font-mono">{r.po_number} / line {r.line_number}</div>
              <div className="text-xs text-muted-foreground">
                {r.supplier_name} • {r.bad_grn_count}{' '}
                {listMode === 'line0' || listMode === 'all' ? 'GRN(s)' : 'open sched(s)'}
                {' • '}{fmtDate(r.last_receipt)}
              </div>
            </button>
          ))}
        </div>
        {count > 50 && (
          <div className="flex gap-2 justify-center mt-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="text-xs self-center">Page {page}</span>
            <Button variant="outline" size="sm" disabled={page * 50 >= count} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        )}
      </Card>

      {/* RIGHT — drag board */}
      <Card className="p-4 overflow-auto">
        {!detail && !detailLoading && (
          <div className="text-muted-foreground text-sm">Pick a PO line on the left.</div>
        )}
        {detailLoading && <Loader2 className="w-4 h-4 animate-spin" />}
        {(error || detailError) && (
          <div className="flex gap-2 text-red-600 text-sm mb-2"><AlertCircle className="w-4 h-4" />{error || detailError}</div>
        )}
        {detail && (
          <GrnRepairBoard
            line={line}
            // GRN Repair shows the same per-slot ERP balance tag and arrear
            // chip as the ERP Balance repair modal and Arrears Repair. The ERP
            // balance is only captured by the install whose import sends it.
            showErp={view === 'grn' && user?.import_handler === 'dennis_eagle'}
            highlightArrears
            header={(
              <div className="mb-3 flex justify-between items-start gap-3">
                <div>
                  <div className="text-lg font-semibold">
                    {detail.po_line.po_number} / line {detail.po_line.line_number}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {detail.po_line.supplier_name} • part {detail.po_line.part_number} • OTIF window: {detail.otif_window.days_early}d early / {detail.otif_window.days_late}d late
                  </div>
                  {view === 'arrears' && (
                    arrearSlots.length > 0 ? (
                      <div className="mt-1 text-sm font-semibold text-red-700">
                        {arrearSlots.length} arrear slot{arrearSlots.length === 1 ? '' : 's'} on this line —
                        balance {arrearSlots.reduce((n, s) => n + parseFloat(s.balance_qty), 0)} still open on a passed date
                      </div>
                    ) : (
                      <div className="mt-1 text-sm font-semibold text-green-700">No arrears left on this line ✓</div>
                    )
                  )}
                </div>
                <div className="flex gap-2">
                  <ClosestMatchButton line={line} />
                  {/* FIFO + split is hidden from the toolbar. It fills schedules
                      earliest-first and SPLITS a receipt across several of them,
                      which contradicts how a delivery actually arrives — one
                      batch, one slot — and leaves part-quantities on slots no
                      single receipt ever satisfied. Closest-match is the
                      behaviour we want operators reaching for. The backend
                      endpoint (`repair-po-line-rebalance-fifo`) is deliberately
                      left in place — still callable for a deliberate one-off, and
                      removing it is a bigger decision than taking the button off
                      the toolbar. */}
                </div>
              </div>
            )}
          />
        )}
      </Card>
      </div>
    </div>
  );
}
